import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { toTextOptions } from '../../../../shared/lib/helpers/ToTextOptions'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { Controller, useForm } from 'react-hook-form'
import React, { useEffect } from 'react'
import { FilterDrop } from '../../../../shared/components/cards/filter-drop/FilterDrop'
import { BpDatePicker } from '../../../../shared/components/forms/datepicker/BpDatePicker'
import { setCanCloseDropdown } from '../../../../app/store/reducers/AppSlice'
import moment from 'moment/moment'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_RANGE_FORMAT,
} from '../../../../shared/consts/DefaultRangeFormat.const'
import {
  selectCountry,
  selectOrganicResultsFilters,
  selectProject,
  setCountry,
  setOrganicResultsFilter,
  setProject,
} from '../../model/organicResultsSlice'
import { OrganicResultsFilterInterface } from '../../model/types/OrganicResultsFilter.interface'
import { PolicyOptions } from '../../../../shared/consts/PolicyOptions.const'
import { Switch } from 'antd'
import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { Trans, useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { AddProjectButton } from '../../../../shared/components/buttons/add-project-button/AddProjectButton'
import { EditCountriesButton } from '../../../../shared/components/buttons/edit-countries-button/EditCountriesButton'
import ContentLoader from 'react-content-loader'
import { FilterChips } from '../../../../shared/components/utility/filter-chips/FilterChips'
import { useMenuLayout } from '../../../../app/layouts/main-layout/hooks/UseMenuLayout'
import { ActionsSlot } from '../../../../shared/components/utility/actions-slot/ActionsSlot'
import clsx from 'clsx'
import styles from './OrganicResultsFilters.module.scss'
import {
  GetFilterAdvertiserDomainsApiArg,
  GetFilterAffiliatesApiArg,
  GetFilterKeywordsApiArg,
  useGetDomainTagListQuery,
  useGetFilterAdvertiserDomainsQuery,
  useGetFilterAffiliatesQuery,
  useGetFilterCountryQuery,
  useGetFilterEngineQuery,
  useGetFilterKeywordsQuery,
  useGetFilterProjectsQuery,
} from '../../../../app/api'
import { useSearchState } from '../../../../lib/hooks/useSearchState'
import { useReversedFilters } from '../../../../lib/hooks/useReversedFilters'
import {
  resetFilterFields,
  useRegisterAppliedFilters,
} from '../../../../lib/context/applied-filters/AppliedFiltersProvider'
import { Notify } from '../../../../shared/components/text/notify/Notify'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { ProjectIcon } from '../../../../shared/components/icons/filters/ProjectIcon'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { SearchEngineIcon } from '../../../../shared/components/icons/filters/SearchEngineIcon'
import { PolicyIcon } from '../../../../shared/components/icons/filters/PolicyIcon'
import { TagIcon } from '../../../../shared/components/icons/filters/TagIcon'
import { KeywordIcon } from '../../../../shared/components/icons/filters/KeywordIcon'
import { AffiliateIcon } from '../../../../shared/components/icons/filters/AffiliateIcon'
import { DomainIcon } from '../../../../shared/components/icons/filters/DomainIcon'
import { withSelectedOptions } from '../../../../shared/lib/helpers/WithSelectedOptions'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'
import { restoreAppliedValues } from '../../../../shared/lib/helpers/AppliedValues'

const REVERSED_FILTER_KEYS: Record<string, string> = {
  policy: 'policy',
  keyword: 'keyword_id',
  affiliate: 'affiliate_id',
  tags: 'tag_ids',
}

/** Фильтры, которые сбрасывает Clear filters, и их значения по умолчанию. */
const CLEARABLE_FILTERS: Partial<OrganicResultsFilterInterface> = {
  country: '',
  site: '',
  policy: undefined,
  keyword: '',
  affiliate: '',
  show_affiliates: false,
  show_hidden: false,
  tags: '',
}

const Filter = ({
  searchParams,
  setSearchParams,
}: IWithSearchParamsProps<OrganicResultsFilterInterface>) => {
  const filters = useAppSelector<OrganicResultsFilterInterface>(selectOrganicResultsFilters)
  const {
    country,
    engine_id,
    project,
    dateRange,
    site,
    policy,
    keyword,
    affiliate,
    show_hidden,
    show_affiliates,
    tags,
    reversed_filters,
  } = filters
  const { t } = useTranslation()
  const { menuLayout } = useMenuLayout()
  const { is_demo: isDemo } = useAppSelector(selectProject)
  const currentCountry = useAppSelector(selectCountry)
  const dispatch = useAppDispatch()
  const [search, setSearch] = useSearchState({ keyword: '', affiliate: '', site: '' })

  const {
    getValues,
    setValue,
    control,
    reset,
    formState: { defaultValues },
  } = useForm<OrganicResultsFilterInterface>({
    mode: 'onChange',
    defaultValues: {
      country,
      engine_id,
      project,
      dateRange,
      site,
      policy,
      keyword,
      affiliate,
      show_hidden,
      show_affiliates,
      tags,
      ...searchParams,
    },
  })

  useEffect(() => {
    applyFilter()
  }, [])

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({})

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery(
    { projectId: project ? +project : undefined, withKeywordsState: true },
    { skip: !project },
  )

  const {
    data: engines,
    isFetching: enginesFetching,
    isLoading: enginesLoading,
  } = useGetFilterEngineQuery({ projectId: project ? +project : undefined }, { skip: !project })

  const {
    data: domains,
    isFetching: domainsFetching,
    isLoading: domainsLoading,
  } = useGetFilterAdvertiserDomainsQuery(
    {
      projectId: project ? +project : undefined,
      search: search.site,
      date: dateRange,
      seo: true,
    } as GetFilterAdvertiserDomainsApiArg,
    { skip: !project },
  )

  const {
    data: keywords,
    isFetching: keywordsFetching,
    isLoading: keywordsLoading,
  } = useGetFilterKeywordsQuery(
    {
      projectId: project,
      countryId: country,
      search: search.keyword,
    } as unknown as GetFilterKeywordsApiArg,
    { skip: !project || !country },
  )

  const {
    data: affiliates,
    isFetching: affiliatesFetching,
    isLoading: affiliatesLoading,
  } = useGetFilterAffiliatesQuery(
    {
      date: dateRange,
      isSeo: true,
      projectId: +project,
      search: search.affiliate,
    } as GetFilterAffiliatesApiArg,
    { skip: !project },
  )

  const {
    data: tagOptions,
    isFetching: tagOptionsFetching,
    isLoading: tagOptionsLoading,
  } = useGetDomainTagListQuery({ projectId: Number(project) }, { skip: !project })

  const applyFilterWithReversed = (reversed: string[]) => {
    const values = { ...getValues(), reversed_filters: reversed.join(',') }
    dispatch(setOrganicResultsFilter(values))
    if (setSearchParams) setSearchParams(values)
  }

  const {
    reversedFilters,
    isReversed,
    isReversedApplied,
    toggleReversedFilter,
    toggleReversedFilterAndApply,
    clearReversedFilter,
    resetReversedFilters,
    restoreReversedFilters,
  } = useReversedFilters({
    keys: REVERSED_FILTER_KEYS,
    applied: reversed_filters,
    fromUrl: searchParams?.reversed_filters,
    onApply: applyFilterWithReversed,
  })

  const applyFilter = () => applyFilterWithReversed(reversedFilters)

  useRegisterAppliedFilters({
    values: filters,
    defaults: CLEARABLE_FILTERS,
    onClear: () => {
      resetFilterFields(setValue, CLEARABLE_FILTERS)
      dispatch(setCountry(undefined))
      resetReversedFilters()
      applyFilterWithReversed([])
    },
  })

  const notify = isDemo
    ? 'demo-project'
    : currentCountry?.keywords_state === 'deleted'
      ? 'missing-keywords'
      : undefined

  return (
    <section>
      <form className={clsx(styles.form)}>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue =
                defaultValues?.project ??
                project ??
                projects?.find((option) => !option.is_demo)?.value ??
                projects?.[0]?.value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={ProjectIcon}
                  bottomSection={<AddProjectButton />}
                  defaultValue={defaultValue}
                  disabled={projectsLoading || projectsFetching}
                  fieldSize='lg'
                  loading={projectsLoading || projectsFetching}
                  options={projects}
                  title={t('fields.project')}
                  onChange={(e) => {
                    onChange(e)

                    const option = projects?.find((option) => option.value === e.target.value)

                    if (option) {
                      dispatch(setProject(option))
                    }

                    if (e.target.value !== defaultValue) {
                      setValue('country', '')
                      setValue('policy', undefined)
                    }
                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='project'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue = defaultValues?.engine_id
                ? defaultValues.engine_id
                : engine_id
                  ? engine_id
                  : engines?.length && engines[0].value

              if (!engines) {
                return (
                  <ContentLoader
                    backgroundColor='#f3f3f3'
                    className={clsx(styles.contentLoader)}
                    foregroundColor='#ecebeb'
                    speed={2}
                  >
                    <rect height='100%' rx='8' ry='8' width='100%' x='0' y='0' />
                  </ContentLoader>
                )
              }

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={SearchEngineIcon}
                  defaultValue={defaultValue}
                  disabled={enginesLoading || enginesFetching}
                  fieldSize='lg'
                  loading={enginesLoading || enginesFetching}
                  options={engines}
                  title={t('fields.searchEngine')}
                  onChange={(e) => {
                    onChange(e)
                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='engine_id'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue = defaultValues?.country
                ? defaultValues.country
                : country
                  ? country
                  : countries?.length && countries[0].value

              if (!countries) {
                return (
                  <ContentLoader
                    backgroundColor='#f3f3f3'
                    className={clsx(styles.contentLoader)}
                    foregroundColor='#ecebeb'
                    speed={2}
                  >
                    <rect height='100%' rx='8' ry='8' width='100%' x='0' y='0' />
                  </ContentLoader>
                )
              }

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={CountryIcon}
                  bottomSection={<EditCountriesButton project={{ value: project, isDemo }} />}
                  defaultValue={defaultValue}
                  disabled={countriesLoading || countriesFetching}
                  fieldSize='lg'
                  loading={countriesLoading || countriesFetching}
                  optionRender={renderCountryOption}
                  options={countries}
                  title={t('fields.country')}
                  onChange={(e) => {
                    onChange(e)

                    const option = countries?.find((option) => option.value === e.target.value)
                    dispatch(setCountry(option))

                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='country'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                multiple
                Icon={DomainIcon}
                enableChipLoad={false}
                fieldSize='lg'
                loading={domainsLoading || domainsFetching}
                options={toTextOptions(domains, value)}
                placeholder={t('fields.domain')}
                value={value?.split(',')}
                onChange={(e) => {
                  onChange(
                    [].slice
                      .call(e.target.selectedOptions)
                      .map((o: IOption) => o.value)
                      .join(','),
                  )
                }}
                onDropClose={applyFilter}
                onSearch={setSearch('site')}
              ></BpSelect>
            )}
            control={control}
            name='site'
          />
        </div>
        <div className={clsx(styles.filterDropWrapper)}>
          <FilterDrop
            values={getValues([
              'dateRange',
              'policy',
              'keyword',
              'affiliate',
              'show_affiliates',
              'show_hidden',
              'tags',
            ])}
            onClear={() => {
              setValue('policy', undefined)
              setValue('keyword', '')
              setValue('affiliate', '')
              setValue('show_affiliates', false)
              setValue('show_hidden', false)
              setValue('tags', '')
              resetReversedFilters()
            }}
            onHide={() => {
              restoreAppliedValues(getValues, setValue, filters)
              restoreReversedFilters()
            }}
            onSubmit={applyFilter}
          >
            <Controller
              render={({ field: { onChange, value } }) => {
                return (
                  <BpDatePicker
                    defaultValue={[
                      moment(value, DEFAULT_DATE_FORMAT),
                      moment(value, DEFAULT_RANGE_FORMAT),
                    ]}
                    label={t('fields.date')}
                    onChange={(e, t) => onChange(`${t[0]} - ${t[1]}`)}
                    onOpenChange={(e) => dispatch(setCanCloseDropdown(!e))}
                  />
                )
              }}
              control={control}
              name='dateRange'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  hasTranslate
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('policy')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('policy')}
                    />
                  }
                  Icon={PolicyIcon}
                  label={t('fields.policy')}
                  options={PolicyOptions}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                />
              )}
              control={control}
              name='policy'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('keyword')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('keyword')}
                    />
                  }
                  Icon={KeywordIcon}
                  enableChipLoad={false}
                  label={t('fields.keyword')}
                  loading={keywordsLoading || keywordsFetching}
                  options={withSelectedOptions('keyword', keywords, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('keyword')}
                ></BpSelect>
              )}
              control={control}
              name='keyword'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('affiliate')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('affiliate')}
                    />
                  }
                  Icon={AffiliateIcon}
                  enableChipLoad={false}
                  label={t('fields.affiliate')}
                  loading={affiliatesLoading || affiliatesFetching}
                  options={withSelectedOptions('affiliate', affiliates, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('affiliate')}
                ></BpSelect>
              )}
              control={control}
              name='affiliate'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('tags')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('tags')}
                    />
                  }
                  options={tagOptions?.data?.items?.map((tag: any) => ({
                    text: tag.name,
                    value: String(tag.id),
                  }))}
                  Icon={TagIcon}
                  disabled={tagOptionsFetching || tagOptionsLoading}
                  enableChipLoad={false}
                  label={t('fields.tag')}
                  loading={tagOptionsFetching || tagOptionsLoading}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    const value = [].slice
                      .call(e.target.selectedOptions)
                      .map((o: IOption) => o.value)

                    onChange(value.join(','))
                  }}
                ></BpSelect>
              )}
              control={control}
              name='tags'
            />
            <Controller
              render={({ field }) => (
                <div className={clsx(styles.switchRow)}>
                  <span className={clsx(styles.switchLabel)}>{t('fields.showAffiliates')}</span>
                  <Switch
                    {...field}
                    checked={field.value}
                    defaultChecked={show_affiliates}
                  ></Switch>
                </div>
              )}
              control={control}
              name='show_affiliates'
            />
            <Controller
              render={({ field }) => (
                <div className={clsx(styles.switchRow)}>
                  <span className={clsx(styles.switchLabel)}>{t('fields.showHiddenSites')}</span>
                  <Switch {...field} checked={field.value} defaultChecked={show_hidden}></Switch>
                </div>
              )}
              control={control}
              name='show_hidden'
            />
          </FilterDrop>
        </div>
      </form>
      <div className={clsx(styles.chipsWrapper)}>
        <FilterChips
          applyCallBack={() => {
            resetReversedFilters()
            applyFilterWithReversed([])
          }}
          clearCallBack={(field: any) => {
            setValue(field, '')
            applyFilterWithReversed(clearReversedFilter(field))
          }}
          items={[
            { title: t('fields.date'), field: 'dateRange', value: dateRange, clearable: false },
            {
              title: t('fields.policy'),
              field: 'policy',
              value: policy,
              data: PolicyOptions,
              type: 'multiselect',
              reversible: true,
              reversed: isReversedApplied('policy'),
            },
            {
              title: t('fields.keyword'),
              field: 'keyword',
              value: keyword,
              reversible: true,
              reversed: isReversedApplied('keyword'),
              data: withSelectedOptions('keyword', keywords, keyword),
              type: 'multiselect',
            },
            {
              title: t('fields.affiliate'),
              field: 'affiliate',
              value: affiliate,
              reversible: true,
              reversed: isReversedApplied('affiliate'),
              data: withSelectedOptions('affiliate', affiliates, affiliate),
              type: 'multiselect',
            },
            {
              title: t('fields.tag'),
              field: 'tags',
              value: tags,
              data: tagOptions?.data?.items?.map((tag: any) => ({
                text: tag.name,
                value: String(tag.id),
              })),
              type: 'multiselect',
              reversible: true,
              reversed: isReversedApplied('tags'),
            },
          ]}
          reverseCallBack={toggleReversedFilterAndApply}
        />
        {menuLayout === 'sidebar' && <ActionsSlot />}
      </div>
      {notify && (
        <Notify className={'mt-[20px] -mb-[30px]'}>
          {
            {
              'demo-project': (
                <Trans i18nKey='other.demoProjectSelected'>
                  <NavLink className={clsx(styles.demoBannerLink)} to='/projects' />
                </Trans>
              ),
              'missing-keywords': (
                <Trans i18nKey='other.missingKeywords'>
                  <NavLink
                    className={clsx(styles.demoBannerLink)}
                    to={`/projects/${project}/keywords`}
                  />
                </Trans>
              ),
            }[notify]
          }
        </Notify>
      )}
    </section>
  )
}

export const OrganicResultsFilters = withSearchParams<
  IWithSearchParamsProps<OrganicResultsFilterInterface>,
  OrganicResultsFilterInterface
>(Filter, selectOrganicResultsFilters)
